import * as THREE from "three";
export default class CustomizeableMaterial {
  constructor(pathfinder, textureLibrary) {
    this.pathfinder = pathfinder;
    this.textureLibrary = textureLibrary;
    this.shaderInputs = {
      primaryColor: new THREE.Color(pathfinder.primary_color),
      secondaryColor: new THREE.Color(pathfinder.secondary_color),
      accentColor: new THREE.Color(pathfinder.accent_color),
      patternMap: this.textureLibrary.getPattern(this.pathfinder.pattern),
      patternNodes: []
    };

    this.materials = [];
  }

  update(pathfinder) {
    this.shaderInputs.primaryColor.set(pathfinder.primary_color);
    this.shaderInputs.secondaryColor.set(pathfinder.secondary_color);
    this.shaderInputs.accentColor.set(pathfinder.accent_color);
    this.shaderInputs.patternNodes.forEach(node => {
      node.value = this.textureLibrary.getPattern(pathfinder.pattern);
    });
  }

  _addNodeMaterial(object) {
    const material = new THREE.StandardNodeMaterial();

    const primaryColorNode = new THREE.ColorNode(
      this.shaderInputs.primaryColor
    );
    const secondaryColorNode = new THREE.ColorNode(
      this.shaderInputs.secondaryColor
    );
    const accentColorNode = new THREE.ColorNode(this.shaderInputs.accentColor);

    const colorMasks = new THREE.TextureNode(this.shaderInputs.pattern_id);
    const primaryMask = new THREE.SwitchNode(colorMasks, "r");
    const secondaryMask = new THREE.SwitchNode(colorMasks, "g");
    const accentMask = new THREE.SwitchNode(colorMasks, "b");

    // ? Configurable by pattern, or user
    // ? Scale by creature
    const translate = new THREE.Vector2();
    const rotate = 0;
    const scale = new THREE.Vector2(3, 3);

    const patternNode = new THREE.TextureNode(this.shaderInputs.patternMap);
    patternNode.uv = new THREE.UVTransformNode();
    patternNode.uv.setUvTransform(
      translate.x,
      translate.y,
      scale.x,
      scale.y,
      THREE.Math.degToRad(rotate)
    );

    const pattern = new THREE.OperatorNode(
      accentColorNode,
      patternNode,
      THREE.OperatorNode.MUL
    );

    const patternInverted = new THREE.OperatorNode(
      primaryColorNode,
      new THREE.Math1Node(patternNode, THREE.Math1Node.INVERT),
      THREE.OperatorNode.MUL
    );

    const combinedPattern = new THREE.OperatorNode(
      pattern,
      patternInverted,
      THREE.OperatorNode.ADD
    );

    const patternMasked = new THREE.Math3Node(
      new THREE.ColorNode(0x000000),
      combinedPattern,
      primaryMask,
      THREE.Math3Node.MIX
    );

    const secondaryMasked = new THREE.OperatorNode(
      secondaryMask,
      secondaryColorNode,
      THREE.OperatorNode.MUL
    );
    const accentMasked = new THREE.OperatorNode(
      accentMask,
      accentColorNode,
      THREE.OperatorNode.MUL
    );

    const add_1 = new THREE.OperatorNode(
      patternMasked,
      secondaryMasked,
      THREE.OperatorNode.ADD
    );

    const final_color = new THREE.OperatorNode(
      add_1,
      accentMasked,
      THREE.OperatorNode.ADD
    );

    /**
     * Outputs
     * mtl.color = // albedo (vec3)
     * mtl.alpha = // opacity (float)
     * mtl.roughness = // roughness (float)
     * mtl.metalness = // metalness (float)
     * mtl.normal = // normal (vec3)
     * mtl.emissive = // emissive color (vec3)
     * mtl.ambient = // ambient color (vec3)
     * mtl.shadow = // shadowmap (vec3)
     * mtl.light = // custom-light (vec3)
     * mtl.ao = // ambient occlusion (float)
     * mtl.environment = // reflection/refraction (vec3)
     * mtl.position = // vertex local position (vec3)
     */

    if (this.shaderInputs.overlay) {
      const overlayNode = new THREE.TextureNode(this.shaderInputs.overlay);
      const overlayMask = new THREE.SwitchNode(overlayNode, "w");

      const with_overlay = new THREE.Math3Node(
        final_color,
        overlayNode,
        overlayMask,
        THREE.Math3Node.MIX
      );

      material.color = with_overlay;
    } else {
      material.color = final_color;
    }

    if (this.shaderInputs.mettalic_map) {
      material.metalness = new THREE.SwitchNode(
        new THREE.TextureNode(this.shaderInputs.mettalic_map),
        "r"
      );
    } else {
      material.metalness = new THREE.FloatNode(object.material.metalness);
    }

    if (this.shaderInputs.roughness_map) {
      material.roughness = new THREE.SwitchNode(
        new THREE.TextureNode(this.shaderInputs.roughness_map),
        "r"
      );
    } else {
      material.roughness = new THREE.FloatNode(object.material.roughness);
    }

    if (this.shaderInputs.ao_map) {
      material.ao = new THREE.SwitchNode(
        new THREE.TextureNode(this.shaderInputs.ao_map),
        "r"
      );
    }

    if (this.shaderInputs.alpha_map) {
      material.alphaTest = 0.15;
      material.alpha = new THREE.SwitchNode(
        new THREE.TextureNode(this.shaderInputs.alpha_map),
        "a"
      );
      material.transparent = true;
    } else {
      material.alpha = new THREE.FloatNode(object.material.opacity);
      material.transparent = true;
    }

    if (this.shaderInputs.emission_map) {
      material.emissive = new THREE.OperatorNode(
        new THREE.TextureNode(this.shaderInputs.emission_map),
        secondaryColorNode,
        THREE.OperatorNode.MUL
      );
    }

    if (this.shaderInputs.normal_map) {
      material.normal = new THREE.NormalMapNode(
        new THREE.TextureNode(this.shaderInputs.normal_map)
      );
    }

    material.side = THREE.DoubleSide;
    material.skinning = true;

    object.material = material;

    this.shaderInputs.patternNodes.push(patternNode);
    this.materials.push(material);
  }

  convertMaterial(object) {
    this.textureLibrary
      .getMapNamesForType(this.pathfinder.type)
      .forEach(map => {
        this.shaderInputs[map] = this.textureLibrary.getMap(
          map,
          this.pathfinder.type
        );
      });
    this._addNodeMaterial(object);
  }
}
